import { useState, useRef, useEffect, type KeyboardEvent, type HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Button } from 'antd';
import {
  SendOutlined,
  ExpandOutlined,
  CloseOutlined,
  RobotOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTheme } from '@hooks/useTheme';
import { chatService } from '@modules/chat-agent/services/chatService';
import type { ChatMessage } from '@modules/chat-agent/types/chat.types';

const { TextArea } = Input;

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'agent',
  content:
    "Hi! I'm your PR Intelligence Assistant. Ask me about coverage, reports, or trends.",
  timestamp: Date.now(),
};

interface FloatingChatPanelProps {
  onClose: () => void;
  isDark: boolean;
  /** Drag the floating stack by the panel header (same behavior as FAB drag). */
  headerDragHandlers?: Pick<
    HTMLAttributes<HTMLDivElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
  >;
}

export function FloatingChatPanel({ onClose, isDark, headerDragHandlers }: FloatingChatPanelProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [closing, setClosing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 250);
  };

  const handleExpand = () => {
    navigate('/chat');
    onClose();
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isTyping) return;

    const userMsg: ChatMessage = {
      id: `msg-user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await chatService.sendMessage(trimmed);
      setMessages((prev) => [...prev, response]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const panelClass = [
    'clippy-chat-panel',
    isDark && 'clippy-chat-panel--dark',
    closing && 'clippy-chat-panel--closing',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={panelClass}>
      <div className="clippy-chat-header clippy-chat-header--draggable" {...headerDragHandlers}>
        <div className="clippy-chat-header-avatar">
          <RobotOutlined />
        </div>
        <div className="clippy-chat-header-info">
          <div className="clippy-chat-header-title">PR Intelligence Assistant</div>
          <div className="clippy-chat-header-status">Online</div>
        </div>
        <div className="clippy-chat-header-actions">
          <button onClick={handleExpand} title="Open full chat">
            <ExpandOutlined />
          </button>
          <button onClick={handleClose} title="Close">
            <CloseOutlined />
          </button>
        </div>
      </div>

      <div className="clippy-chat-messages">
        {messages.map((msg) => {
          const isUser = msg.role === 'user';
          return (
            <div
              key={msg.id}
              className={`clippy-msg ${isUser ? 'clippy-msg--user' : 'clippy-msg--agent'}`}
            >
              <div
                className={`clippy-msg-avatar ${isUser ? 'clippy-msg-avatar--user' : 'clippy-msg-avatar--agent'}`}
              >
                {isUser ? <UserOutlined /> : <RobotOutlined />}
              </div>
              <div className="clippy-msg-content">
                <div className="clippy-msg-bubble">{msg.content}</div>
                <span className="clippy-msg-time">
                  {new Date(msg.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="clippy-typing">
            <div className="clippy-typing-dots">
              <span />
              <span />
              <span />
            </div>
            Agent is typing...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="clippy-chat-input-area">
        <TextArea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask something..."
          autoSize={{ minRows: 1, maxRows: 3 }}
          disabled={isTyping}
          style={{ borderRadius: 10, fontSize: 13 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={!inputValue.trim() || isTyping}
          style={{
            height: 36,
            width: 36,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #6366f1, #0ea5e9)',
            border: 'none',
            flexShrink: 0,
          }}
        />
      </div>
    </div>
  );
}
